"use client";

import { useCallback, useEffect, useState } from "react";
import { AdminAuthError, adminFetch, getAdminKey } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { Badge, Button, Card, Spinner, inputClass } from "@/components/ui";

interface ProductTwin { id: string; title: string; source: string }
interface ViralJob {
  id: string;
  sourceMode: string;
  sourceUrl: string | null;
  sourceUploadId: string | null;
  platforms: string[];
  keywords: string;
  lookbackDays: number;
  minViews: number;
  productTwinId: string;
  status: string;
  candidateCount: number;
  error: string | null;
  createdAt: string;
}
interface ResponseData { jobs: ViralJob[]; products: ProductTwin[] }

const PLATFORM_OPTIONS = [
  ["tiktok", "TikTok"],
  ["instagram", "Instagram Reels"],
  ["youtube", "YouTube Shorts"],
] as const;

export default function ViralRemixPage() {
  const [data, setData] = useState<ResponseData | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceMode, setSourceMode] = useState("scan");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceUploadId, setSourceUploadId] = useState("");
  const [uploadedFilename, setUploadedFilename] = useState("");
  const [uploading, setUploading] = useState(false);
  const [platforms, setPlatforms] = useState<string[]>(["tiktok", "instagram", "youtube"]);
  const [keywords, setKeywords] = useState("");
  const [lookbackDays, setLookbackDays] = useState(7);
  const [minViews, setMinViews] = useState(100000);
  const [productTwinId, setProductTwinId] = useState("");

  const load = useCallback(async () => {
    try {
      const next = await adminFetch<ResponseData>("/api/admin/viral-remix");
      setData(next);
      setProductTwinId((current) => current || next.products[0]?.id || "");
      setNeedKey(false);
    } catch (cause) {
      if (cause instanceof AdminAuthError) setNeedKey(true);
      else setError(cause instanceof Error ? cause.message : "Load failed");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function createJob() {
    setBusy(true);
    setError(null);
    try {
      await adminFetch("/api/admin/viral-remix", {
        method: "POST",
        body: JSON.stringify({ sourceMode, sourceUrl, sourceUploadId, platforms, keywords, lookbackDays, minViews, productTwinId }),
      });
      setSourceUrl("");
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  async function uploadVideo(file: File) {
    setUploading(true);
    setError(null);
    try {
      const prepared = await adminFetch<{ uploadUrl?: string; item: { id: string } }>("/api/admin/viral-remix/upload", {
        method: "POST",
        body: JSON.stringify({ filename: file.name, mime: file.type, size: file.size }),
      });
      if (prepared.uploadUrl) {
        const sent = await fetch(prepared.uploadUrl, { method: "PUT", headers: { "content-type": file.type }, body: file });
        if (!sent.ok) throw new Error("Video upload failed");
        await adminFetch("/api/admin/viral-remix/upload", { method: "POST", body: JSON.stringify({ action: "complete", id: prepared.item.id }) });
      } else {
        const form = new FormData();
        form.set("file", file);
        const response = await fetch("/api/admin/viral-remix/upload", { method: "POST", credentials: "same-origin", headers: { "x-admin-key": getAdminKey() }, body: form });
        if (!response.ok) throw new Error(((await response.json().catch(() => ({}))) as { error?: string }).error ?? "Video upload failed");
      }
      setSourceUploadId(prepared.item.id);
      setUploadedFilename(file.name);
    } catch (cause) {
      setSourceUploadId("");
      setUploadedFilename("");
      setError(cause instanceof Error ? cause.message : "Video upload failed");
    } finally {
      setUploading(false);
    }
  }

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;
  if (!data) return <div className="grid min-h-[60vh] place-items-center"><Spinner className="size-6" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:py-10">
      <div className="mb-8">
        <div className="flex items-center gap-3"><span className="text-2xl">🔥</span><h1 className="text-2xl font-semibold tracking-tight">Viral Analyze</h1></div>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-muted">Standalone viral video discovery and analysis workspace. It learns a reference video&apos;s hook, structure, shots and pacing, then creates a new plan using the selected customer product. It is independent from Workflow and Templates.</p>
      </div>

      {error && <div className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}

      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <Card className="h-fit p-5">
          <h2 className="font-semibold">New analysis job</h2>
          <div className="mt-5 space-y-5">
            <label className="block text-xs text-muted">Source mode
              <select className={`${inputClass} mt-2`} value={sourceMode} onChange={(e) => setSourceMode(e.target.value)}>
                <option value="scan">Scan website by views + keywords</option>
                <option value="upload">Upload a video directly</option>
              </select>
            </label>
            {sourceMode === "scan" && <>
              <label className="block text-xs text-muted">Website / channel URL
                <input className={`${inputClass} mt-2`} value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="Website, TikTok profile or YouTube channel URL" />
              </label>
              <div><div className="mb-2 text-xs text-muted">Website type</div><div className="grid grid-cols-3 gap-2">
                {PLATFORM_OPTIONS.map(([value, label]) => <label key={value} className="rounded-lg border border-line px-2 py-2 text-center text-[11px]"><input className="mr-1" type="checkbox" checked={platforms.includes(value)} onChange={() => setPlatforms((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value])} />{label}</label>)}
              </div></div>
              <label className="block text-xs text-muted">Video keywords
                <input className={`${inputClass} mt-2`} value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="skincare, breast pump, travel gadget…" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-muted">Lookback days<input className={`${inputClass} mt-2`} type="number" min={1} max={90} value={lookbackDays} onChange={(e) => setLookbackDays(Number(e.target.value))} /></label>
                <label className="text-xs text-muted">Minimum views<input className={`${inputClass} mt-2`} type="number" min={0} step={10000} value={minViews} onChange={(e) => setMinViews(Number(e.target.value))} /></label>
              </div>
            </>}
            {sourceMode === "upload" && <div>
              <div className="mb-2 text-xs text-muted">Video file</div>
              <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-line bg-white/[0.02] px-4 text-center hover:border-[var(--accent)]/50">
                {uploading ? <><Spinner /><span className="mt-2 text-xs">Uploading…</span></> : uploadedFilename ? <><span className="text-xl">✓</span><span className="mt-2 text-xs text-emerald-300">{uploadedFilename}</span><span className="mt-1 text-[10px] text-muted">Click to replace</span></> : <><span className="text-xl">⬆</span><span className="mt-2 text-xs">Upload MP4, MOV or WebM</span><span className="mt-1 text-[10px] text-muted">Maximum 500MB</span></>}
                <input className="hidden" type="file" accept="video/mp4,video/quicktime,video/webm" disabled={uploading} onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadVideo(file); }} />
              </label>
            </div>}
            <label className="block text-xs text-muted">Customer product
              <select className={`${inputClass} mt-2`} value={productTwinId} onChange={(e) => setProductTwinId(e.target.value)}>
                {data.products.length === 0 && <option value="">No product twins available</option>}
                {data.products.map((product) => <option key={product.id} value={product.id}>{product.title}</option>)}
              </select>
            </label>
            <Button className="w-full" disabled={busy || uploading || !productTwinId || (sourceMode === "scan" ? !sourceUrl : !sourceUploadId)} onClick={() => void createJob()}>{busy ? <><Spinner /> Creating…</> : "Create analysis job"}</Button>
            <p className="text-[11px] leading-5 text-muted">Only the creative grammar is reused. Original video, music, creator identity and unverified claims are excluded from the output.</p>
          </div>
        </Card>

        <div>
          <div className="mb-3 flex items-center justify-between"><h2 className="font-semibold">Analysis jobs</h2><Badge tone="neutral">{data.jobs.length}</Badge></div>
          <div className="space-y-3">
            {data.jobs.length === 0 && <Card className="p-8 text-center text-sm text-muted">No viral remix jobs yet.</Card>}
            {data.jobs.map((job) => {
              const product = data.products.find((item) => item.id === job.productTwinId);
              return <Card key={job.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><div className="font-medium">{product?.title ?? job.productTwinId}</div><div className="mt-1 text-xs text-muted">{job.sourceMode === "upload" ? `Uploaded video · ${job.sourceUploadId}` : `${job.sourceUrl} · ${job.keywords || "all matching videos"} · ≥ ${job.minViews.toLocaleString()} views`}</div></div>
                  <Badge tone={job.status === "failed" ? "danger" : job.status === "ready" ? "success" : "warning"}>{job.status}</Badge>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-xs"><div><span className="text-muted">Mode</span><div className="mt-1">{job.sourceMode}</div></div><div><span className="text-muted">Candidates</span><div className="mt-1">{job.candidateCount}</div></div><div><span className="text-muted">Created</span><div className="mt-1">{new Date(job.createdAt).toLocaleString()}</div></div></div>
                {job.error && <p className="mt-3 text-xs text-red-300">{job.error}</p>}
              </Card>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
